/**
 * Resolve sugestão de orçamento por categoria (média histórica ou alvo do método).
 */

const SUGGESTION_EPSILON = 0.01;

function eligibleCategories(group) {
  return (group?.categories || []).filter(
    (cat) => !cat.is_smoothing_pot && !cat.is_archived,
  );
}

/**
 * Arredonda para cima ao múltiplo de €10 («média + folga»).
 * @param {number} amount
 */
export function roundUpBudgetSuggestion(amount) {
  if (!Number.isFinite(amount) || amount <= 0) return amount;
  return Math.ceil(amount / 10) * 10;
}

/**
 * @returns {{ suggested: number, basis: 'average'|'method_target', average: number|null } | null}
 */
export function resolveCategoryBudgetSuggestion(category, group) {
  if (!category || category.is_smoothing_pot) return null;

  const currentBudget = Number(category.budget_eur ?? 0);
  const apiSuggested =
    category.suggested_budget_eur != null
      ? Number(category.suggested_budget_eur)
      : null;
  const average =
    category.average_spent_eur != null
      ? Number(category.average_spent_eur)
      : null;

  if (apiSuggested != null && Number.isFinite(apiSuggested)) {
    const basis =
      category.suggestion_basis
      || (average != null ? 'average' : 'method_target');
    const suggested =
      basis === 'average'
        ? roundUpBudgetSuggestion(apiSuggested)
        : apiSuggested;
    if (Math.abs(currentBudget - suggested) <= SUGGESTION_EPSILON) {
      return null;
    }
    return { suggested, basis, average };
  }

  if (average != null && Number.isFinite(average) && average > 0) {
    const suggested = roundUpBudgetSuggestion(average);
    if (Math.abs(currentBudget - suggested) <= SUGGESTION_EPSILON) {
      return null;
    }
    return { suggested, basis: 'average', average };
  }

  const target = Number(group?.target_budget_eur ?? 0);
  if (!Number.isFinite(target) || target <= 0) return null;

  const cats = eligibleCategories(group);
  if (!cats.length) return null;

  const share = Math.round((target / cats.length) * 100) / 100;
  if (Math.abs(currentBudget - share) <= SUGGESTION_EPSILON) return null;

  return { suggested: share, basis: 'method_target', average: null };
}

export function groupHasSpendingHistory(group) {
  return group?.average_spent_eur != null;
}

/**
 * True when no group has closed-cycle spending averages.
 */
export function isFirstCycleWithoutHistory(groups = []) {
  if (!groups.length) return false;
  return groups.every((group) => !groupHasSpendingHistory(group));
}

/**
 * Collect method_target (or average) suggestions to apply as starter plan.
 */
export function collectStarterPlanUpdates(groups = []) {
  const updates = [];
  for (const group of groups) {
    for (const category of group.categories || []) {
      if (category.is_smoothing_pot || category.is_archived) continue;
      const resolved = resolveCategoryBudgetSuggestion(category, group);
      if (!resolved) continue;
      const categoryId = category.category_id || category.id;
      if (!categoryId) continue;
      updates.push({
        categoryId,
        amount: resolved.suggested.toFixed(2),
      });
    }
  }
  return updates;
}
